// Writes, one hook per resource, all built from the same three shapes.
//
// The §06 write conventions are uniform across all eight resources — POST a
// `*Create` body with an `Idempotency-Key`, PATCH a partial `*Update` body with
// `If-Match: <version>`, DELETE with the same `If-Match` — so there is one
// implementation and eight typed bindings of it. A per-resource hand-written
// mutation is how one of them ends up forgetting the version header and getting
// a 428 in production.
//
// Cache invalidation is declared per resource below rather than decided at each
// call site, and it is deliberately NOT `queryClient.clear()`. Nuking the cache
// after renaming a partner re-fetches every report on the dashboard; naming the
// keys that actually went stale keeps a save feeling like a save.

import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import type {
  CreateInvoiceDraftRequest,
  InvoiceCreate,
  InvoiceIssue,
  InvoiceLineCreate,
  InvoiceLineUpdate,
  InvoiceUpdate,
} from "@crm/shared";
import type { z } from "zod";
import {
  Agreement,
  AgreementImplFeeTier,
  BillingContact,
  AgreementTerm,
  CreateInvoiceDraftResponse,
  Deal,
  DealFact,
  DealStatement,
  FxRate,
  IngestBatchResult,
  InvoiceIssueResult,
  InvoiceLineListItem,
  InvoiceListItem,
  Note,
  Partner,
  PrepaidEntitlement,
  ProjectionSettings,
  TermLine,
  UsageJob,
} from "@crm/shared";
import type {
  NoteCreate,
  NoteUpdate,
  StartUsageJobRequest,
  AgreementCreate,
  AgreementImplFeeTierCreate,
  AgreementImplFeeTierUpdate,
  BillingContactCreate,
  BillingContactUpdate,
  AgreementTermCreate,
  AgreementTermUpdate,
  AgreementUpdate,
  DealCreate,
  DealUpdate,
  FxRateCreate,
  FxRateUpdate,
  IngestDealFactsRequest,
  IngestProviderCostFactsRequest,
  PartnerCreate,
  PartnerUpdate,
  PrepaidEntitlementCreate,
  PrepaidEntitlementUpdate,
  PreviewDealStatementRequest,
  ProjectionSettingsUpdate,
  TermLineCreate,
  TermLineUpdate,
} from "@crm/shared";
import { useCallback } from "react";
import { apiDelete, apiGet, apiWrite } from "./client.js";

/** Every writable collection path, spelled once. */
export type CrmResource =
  | "partners"
  | "agreements"
  | "agreement-terms"
  | "agreement-impl-fee-tiers"
  | "deals"
  | "billing-contacts"
  | "term-lines"
  | "deal-facts"
  | "prepaid-entitlements"
  | "fx-rates"
  | "invoices"
  | "invoice-lines"
  | "notes";

/**
 * Which cached queries a write to each resource makes stale. Key PREFIXES —
 * TanStack matches from the left, so `["deals"]` covers every filter/sort/page
 * variant of the deals list without listing them.
 *
 * The non-obvious entries are the point:
 *   * a partner rename changes `partner_name` on every deals row (§06's
 *     read-side denormalisation), so the deals list is stale too;
 *   * a partner write can also MOVE the partner under a different owner, and
 *     the agreements a deal may inherit or pin are the ones on its partner's
 *     ownership chain (`available_to_partner_id`). So a partner write restates
 *     the agreement lists as well — without that, a deal form opened after a
 *     re-parenting offers the old owner's paper and is refused on save;
 *   * an agreement's `is_default` decides which terms every INHERITING deal
 *     under that partner and its owned companies resolves through, so promoting
 *     one moves real money on the reports — not just a name in a picker;
 *   * a term is embedded in its agreement's DETAIL response, so an added
 *     effective-dated term invalidates `["agreement", id]`, not a terms list;
 *   * an implementation-fee band is embedded one level deeper still — inside a
 *     term, inside that same agreement detail — and it is what the
 *     implementation-fee report prices every inheriting deal from, so editing a
 *     band restates the suggestion on deal screens that never mention it;
 *   * NOTHING stores a charge any more, so every write that could change what a
 *     period is worth restates `["deal-statement"]`: a term line is the rule, a
 *     deal fact is an input to it, a pool allocates across it, and the share
 *     comes off the agreement. A statement is re-derived, so the cache is the
 *     only thing that can be out of date;
 *   * anything that moves money, dates or splits invalidates `["report"]`,
 *     because the dashboard is derived from all of it.
 */
const STALE_AFTER: Record<CrmResource, readonly (readonly string[])[]> = {
  partners: [
    ["partners"],
    ["partner"],
    ["agreements"],
    ["agreement"],
    ["deals"],
    ["deal"],
    ["deal-statement"],
    ["report"],
  ],
  agreements: [["agreements"], ["agreement"], ["deals"], ["deal"], ["deal-statement"], ["report"]],
  "agreement-terms": [["agreement"], ["agreements"], ["deal-statement"], ["report"]],
  "agreement-impl-fee-tiers": [["agreement"], ["agreements"], ["deal-statement"], ["report"]],
  deals: [["deals"], ["deal"], ["deal-statement"], ["report"]],
  // A contact changes no figure. It does change who a deal's invoices go to,
  // which the deal reads carry resolved (`effective_billing_contacts`).
  "billing-contacts": [["billing-contacts"], ["deals"], ["deal"]],
  // `["term-line-revisions"]` is the append-only history: every save writes one,
  // and a revision list that did not re-read would say an edit never happened.
  "term-lines": [
    ["term-lines"],
    ["term-line-revisions"],
    ["deal-statement"],
    ["prepaid-entitlements"],
    ["report"],
  ],
  "deal-facts": [["deal-facts"], ["deal-statement"], ["report"]],
  "prepaid-entitlements": [["prepaid-entitlements"], ["deal-statement"], ["report"]],
  "fx-rates": [["fx-rates"], ["report"]],
  // A line changes what its document totals, so writing one restates the
  // invoice it is on as well as the list. `["deal-statement"]` is there for
  // issuing, which freezes the period: from then on the statement returns the
  // snapshot rather than a fresh interpretation.
  invoices: [["invoices"], ["invoice"], ["invoice-lines"], ["deal-statement"], ["report"]],
  "invoice-lines": [["invoice-lines"], ["invoices"], ["invoice"], ["deal-statement"], ["report"]],
  // A note changes no figure, so no report and no statement is stale. `["search"]`
  // is: the global box and the note search both read notes, and a note saved
  // and then not found by a search run a second later reads as a bug.
  notes: [["notes"], ["note"], ["note-search"], ["search"]],
};

async function invalidate(queryClient: QueryClient, resource: CrmResource): Promise<void> {
  await Promise.all(
    STALE_AFTER[resource].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  );
}

export interface CreateVars<TBody> {
  body: TBody;
  /** Stable across retries of this submission — see `useIdempotencyKey`. */
  idempotencyKey: string;
}

export interface UpdateVars<TBody> {
  id: string;
  body: TBody;
  /** The version the form LOADED. A mismatch is a 409, which is the point. */
  version: number;
}

export interface DeleteVars {
  id: string;
  version: number;
}

export interface ResourceMutations<TRecord, TCreate, TUpdate> {
  create: UseMutationResult<TRecord, Error, CreateVars<TCreate>>;
  update: UseMutationResult<TRecord, Error, UpdateVars<TUpdate>>;
  remove: UseMutationResult<void, Error, DeleteVars>;
  /**
   * Re-read ONE record, bypassing the cache. This is what the 409 stale-copy
   * flow reloads: after a conflict the cache is as stale as the form is, and
   * the user is being shown "here is what is there now" — which has to be
   * fetched, not remembered.
   */
  fetchCurrent: (id: string) => Promise<TRecord>;
}

/**
 * `retry: false` throughout. A write is not a read: replaying a POST because
 * the socket hiccuped is exactly what `Idempotency-Key` makes safe, but
 * replaying it without the user asking hides the failure — and a 409 or a 400
 * retried automatically just produces the same answer while the dialog sits on
 * a spinner. The user decides whether to try again.
 */
export function useResourceMutations<TRecord, TCreate, TUpdate>(
  resource: CrmResource,
  schema: z.ZodType<TRecord>,
): ResourceMutations<TRecord, TCreate, TUpdate> {
  const queryClient = useQueryClient();

  const create = useMutation<TRecord, Error, CreateVars<TCreate>>({
    mutationFn: ({ body, idempotencyKey }) =>
      apiWrite(schema, "POST", `/${resource}`, body, { idempotencyKey }),
    onSuccess: () => invalidate(queryClient, resource),
    retry: false,
  });

  const update = useMutation<TRecord, Error, UpdateVars<TUpdate>>({
    mutationFn: ({ id, body, version }) =>
      apiWrite(schema, "PATCH", `/${resource}/${id}`, body, { ifMatch: version }),
    onSuccess: () => invalidate(queryClient, resource),
    retry: false,
  });

  const remove = useMutation<void, Error, DeleteVars>({
    mutationFn: ({ id, version }) => apiDelete(`/${resource}/${id}`, { ifMatch: version }),
    onSuccess: () => invalidate(queryClient, resource),
    retry: false,
  });

  const fetchCurrent = useCallback(
    (id: string) => apiGet(schema, `/${resource}/${id}`),
    [resource, schema],
  );

  return { create, update, remove, fetchCurrent };
}

// ---------------------------------------------------------------------------
// The eight bindings. Each one pins the read schema and the two input schemas
// together, so a screen cannot accidentally PATCH a deal with a partner body.
// ---------------------------------------------------------------------------

export function usePartnerMutations(): ResourceMutations<Partner, PartnerCreate, PartnerUpdate> {
  return useResourceMutations("partners", Partner);
}

/**
 * Notes. `create` is how a person adds one by hand; the notetaker feed writes
 * through the same endpoint with a `source` and an `external_ref`.
 */
export function useNoteMutations(): ResourceMutations<Note, NoteCreate, NoteUpdate> {
  return useResourceMutations("notes", Note);
}

export function useAgreementMutations(): ResourceMutations<
  Agreement,
  AgreementCreate,
  AgreementUpdate
> {
  return useResourceMutations("agreements", Agreement);
}

export function useAgreementTermMutations(): ResourceMutations<
  AgreementTerm,
  AgreementTermCreate,
  AgreementTermUpdate
> {
  return useResourceMutations("agreement-terms", AgreementTerm);
}

/**
 * Implementation-fee bands. Bound to the flat tier record, not to a term: the
 * bands are a resource of their own on the wire even though they are only ever
 * READ through their agreement's detail response, which is why the invalidation
 * above names `["agreement"]` and there is no `["impl-fee-tiers"]` list to
 * refresh.
 */
export function useAgreementImplFeeTierMutations(): ResourceMutations<
  AgreementImplFeeTier,
  AgreementImplFeeTierCreate,
  AgreementImplFeeTierUpdate
> {
  return useResourceMutations("agreement-impl-fee-tiers", AgreementImplFeeTier);
}

export function useBillingContactMutations(): ResourceMutations<
  BillingContact,
  BillingContactCreate,
  BillingContactUpdate
> {
  return useResourceMutations("billing-contacts", BillingContact);
}

export function useDealMutations(): ResourceMutations<Deal, DealCreate, DealUpdate> {
  return useResourceMutations("deals", Deal);
}

/**
 * A deal's clauses. Bound to the LIST ITEM schema, not a bare record: the
 * endpoints answer with the customer's name and the deal's currency alongside
 * the clauses, and a saved line is rendered by the same row the list draws.
 */
export function useTermLineMutations(): ResourceMutations<
  TermLine,
  TermLineCreate,
  TermLineUpdate
> {
  return useResourceMutations("term-lines", TermLine);
}

/**
 * Pools. Created and edited here because a pool is bought, not measured: what
 * it holds, the window it covers and the lines it covers are all things the
 * paper says.
 */
export function usePrepaidEntitlementMutations(): ResourceMutations<
  PrepaidEntitlement,
  PrepaidEntitlementCreate,
  PrepaidEntitlementUpdate
> {
  return useResourceMutations("prepaid-entitlements", PrepaidEntitlement);
}

/**
 * Deal facts: DELETE only, and that is the model rather than an omission.
 *
 * A fact has no PATCH. A correction is a re-ingest of the same key, which is
 * what makes a person's write and a feed's write one operation — see
 * `useIngestDealFactsMutation`. `create` and `update` are typed `never` so a
 * screen cannot reach for an endpoint the document does not declare; `remove`
 * and `fetchCurrent` are the two this binding exists for.
 */
export function useDealFactMutations(): ResourceMutations<DealFact, never, never> {
  return useResourceMutations("deal-facts", DealFact);
}

export function useFxRateMutations(): ResourceMutations<FxRate, FxRateCreate, FxRateUpdate> {
  return useResourceMutations("fx-rates", FxRate);
}

export function useInvoiceMutations(): ResourceMutations<
  InvoiceListItem,
  InvoiceCreate,
  InvoiceUpdate
> {
  return useResourceMutations("invoices", InvoiceListItem);
}

export function useInvoiceLineMutations(): ResourceMutations<
  InvoiceLineListItem,
  InvoiceLineCreate,
  InvoiceLineUpdate
> {
  return useResourceMutations("invoice-lines", InvoiceLineListItem);
}

// ---------------------------------------------------------------------------
// The operations that are not a row.
//
// Not `useResourceMutations`, and the difference is the whole reason they
// exist: an ingest writes many facts in one audited batch, issuing freezes a
// whole period, and a preview writes nothing at all. None of them has an id to
// PATCH or a single `version` to pin. What they share with every other write
// here is the invalidation, declared once above.
//
// `retry: false` for the reason stated above: replaying a batch because a
// socket hiccuped is not a decision this layer gets to make.
// ---------------------------------------------------------------------------

export interface IngestDealFactsVars {
  body: IngestDealFactsRequest;
  /** Stable across retries of this submission — see `useIdempotencyKey`. */
  idempotencyKey: string;
}

/**
 * How a PERSON writes a figure, and how a feed writes one: the same operation.
 *
 * The SPA posts a one-row batch with `source: "person"`, and it lands on the
 * same table under the same upsert-by-key rules a feed uses. That is what makes
 * "Record a fact" one form for any fact kind rather than three forms that drift.
 */
export function useIngestDealFactsMutation(): UseMutationResult<
  IngestBatchResult,
  Error,
  IngestDealFactsVars
> {
  const queryClient = useQueryClient();
  return useMutation<IngestBatchResult, Error, IngestDealFactsVars>({
    mutationFn: ({ body, idempotencyKey }) =>
      apiWrite(IngestBatchResult, "POST", "/ingest/v1/deal-facts", body, { idempotencyKey }),
    onSuccess: () => invalidate(queryClient, "deal-facts"),
    retry: false,
  });
}

export interface IngestProviderCostFactsVars {
  body: IngestProviderCostFactsRequest;
  idempotencyKey: string;
}

/** The same path for what a provider charged us. No screen writes it yet. */
export function useIngestProviderCostFactsMutation(): UseMutationResult<
  IngestBatchResult,
  Error,
  IngestProviderCostFactsVars
> {
  const queryClient = useQueryClient();
  return useMutation<IngestBatchResult, Error, IngestProviderCostFactsVars>({
    mutationFn: ({ body, idempotencyKey }) =>
      apiWrite(IngestBatchResult, "POST", "/ingest/v1/provider-cost-facts", body, {
        idempotencyKey,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["provider-cost-facts"] });
      await queryClient.invalidateQueries({ queryKey: ["deal-statement"] });
    },
    retry: false,
  });
}

export interface PreviewDealStatementVars {
  dealId: string;
  body: PreviewDealStatementRequest;
}

/**
 * "Try it": run one unsaved line over a period and show what it would bill.
 *
 * A POST that writes nothing, which is why the form may call it on every press
 * without asking anybody first. It carries no `Idempotency-Key` — the operation
 * declares none, because a replay of a read-only evaluation is the evaluation
 * again — and it invalidates nothing, because nothing went stale.
 */
export function usePreviewDealStatementMutation(): UseMutationResult<
  DealStatement,
  Error,
  PreviewDealStatementVars
> {
  return useMutation<DealStatement, Error, PreviewDealStatementVars>({
    mutationFn: ({ dealId, body }) =>
      apiWrite(DealStatement, "POST", `/deals/${dealId}/statement/preview`, body),
    retry: false,
  });
}

export interface IssueInvoiceVars {
  id: string;
  body: InvoiceIssue;
  idempotencyKey?: string;
}

export function useIssueInvoiceMutation(): UseMutationResult<
  InvoiceIssueResult,
  Error,
  IssueInvoiceVars
> {
  const queryClient = useQueryClient();
  return useMutation<InvoiceIssueResult, Error, IssueInvoiceVars>({
    mutationFn: ({ id, body, idempotencyKey }) =>
      apiWrite(InvoiceIssueResult, "POST", `/invoices/${id}/issue`, body, { idempotencyKey }),
    onSuccess: async () => {
      await invalidate(queryClient, "invoices");
      await invalidate(queryClient, "deal-facts");
    },
    retry: false,
  });
}

export interface CreateInvoiceDraftVars {
  body: CreateInvoiceDraftRequest;
  idempotencyKey: string;
}

/**
 * Build the draft invoice for one customer's month, from the close itself.
 *
 * Idempotent per customer and month on the server: pressing it again answers
 * with the draft that already exists (`created: false`), so the button is safe
 * to offer every time and simply opens the document.
 */
export function useCreateInvoiceDraftMutation(): UseMutationResult<
  CreateInvoiceDraftResponse,
  Error,
  CreateInvoiceDraftVars
> {
  const queryClient = useQueryClient();
  return useMutation<CreateInvoiceDraftResponse, Error, CreateInvoiceDraftVars>({
    mutationFn: ({ body, idempotencyKey }) =>
      apiWrite(CreateInvoiceDraftResponse, "POST", "/invoices/draft", body, { idempotencyKey }),
    onSuccess: async () => {
      await invalidate(queryClient, "invoices");
    },
    retry: false,
  });
}

export interface StartUsageJobVars {
  body: StartUsageJobRequest;
  idempotencyKey: string;
}

/**
 * `Refresh` on a cost screen: queue a rollup pass over the month in view.
 *
 * A write, because it spends database time — but it writes no figure. The job
 * carries no result either: when it finishes, the report is re-read, which is
 * what keeps one answer in one place. Two people pressing this on one screen
 * produce ONE job (a partial unique index on the queue), so the response may
 * name a job that was already running.
 */
export function useStartUsageJobMutation(): UseMutationResult<UsageJob, Error, StartUsageJobVars> {
  const queryClient = useQueryClient();
  return useMutation<UsageJob, Error, StartUsageJobVars>({
    mutationFn: ({ body, idempotencyKey }) =>
      apiWrite(UsageJob, "POST", "/reports/usage-jobs", body, { idempotencyKey }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["usage-job"] });
    },
    retry: false,
  });
}

export interface UpdateProjectionSettingsVars {
  body: ProjectionSettingsUpdate;
  /** The settings version the scenario was read against. A mismatch is a 409. */
  version: number;
}

/**
 * Save a scenario as the default assumptions. A singleton rather than a row
 * with an id, so not `useResourceMutations` — but the same `If-Match`
 * convention: two people saving over each other is a 409, never a silent
 * last-write-wins. Every projection reading falls back to these, so every
 * cached projection is stale after a save.
 */
export function useUpdateProjectionSettingsMutation(): UseMutationResult<
  ProjectionSettings,
  Error,
  UpdateProjectionSettingsVars
> {
  const queryClient = useQueryClient();
  return useMutation<ProjectionSettings, Error, UpdateProjectionSettingsVars>({
    mutationFn: ({ body, version }) =>
      apiWrite(ProjectionSettings, "PATCH", "/projection-settings", body, { ifMatch: version }),
    onSuccess: async (saved) => {
      queryClient.setQueryData(["projection-settings"], saved);
      await queryClient.invalidateQueries({ queryKey: ["report", "projection"] });
    },
    retry: false,
  });
}
